import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiEdit2, FiTrash2, FiUpload, FiPlay, FiPause, FiX } from 'react-icons/fi'
import { themes, uploads, type ThemeDetail, type ThemeImage } from '../api/client'
import { styles } from './adminStyles'

/* ── Theme Detail View ───────────────────────────────────────── */

interface ThemeDetailViewProps {
  slug:   string
  onBack: () => void
}

interface ImageForm {
  wordEs:       string
  wordEn:       string
  altEs:        string
  altEn:        string
  definitionEs: string
  definitionEn: string
  value:        string   // cents as a string input; empty = null
}

const emptyImageForm = (): ImageForm => ({
  wordEs: '', wordEn: '', altEs: '', altEn: '', definitionEs: '', definitionEn: '', value: '',
})

// One entry in the batch queue: a File + a Spanish name
interface BatchEntry {
  file:    File
  wordEs:  string
  preview: string   // object URL
  status:  'pending' | 'uploading' | 'done' | 'error'
  error?:  string
}

export function ThemeDetailView({ slug, onBack }: ThemeDetailViewProps) {
  const navigate = useNavigate()
  const [theme, setTheme]         = useState<ThemeDetail | null>(null)
  const [loading, setLoading]     = useState(true)
  const [showForm, setShowForm]   = useState<'single' | 'batch' | false>(false)
  const [imageForm, setImageForm] = useState<ImageForm>(emptyImageForm())
  const [file, setFile]           = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // Batch upload state
  const [batchEntries, setBatchEntries] = useState<BatchEntry[]>([])
  const [batchUploading, setBatchUploading] = useState(false)
  const [batchError, setBatchError] = useState<string | null>(null)
  const batchFileRef = useRef<HTMLInputElement>(null)

  // Image edit state
  const [editingImageId, setEditingImageId] = useState<number | null>(null)
  const [imageEditForm, setImageEditForm]   = useState<ImageForm>(emptyImageForm())
  const [imageEditSaving, setImageEditSaving] = useState(false)
  const [imageEditError, setImageEditError]   = useState<string | null>(null)

  // Generate additional items state
  const [showGenItems, setShowGenItems]     = useState(false)
  const [genItemsCount, setGenItemsCount]   = useState(6)
  const [genItemsPrompt, setGenItemsPrompt] = useState('')
  const [genItemsLoading, setGenItemsLoading] = useState(false)
  const [genItemsError, setGenItemsError]   = useState<string | null>(null)

  // Dismissed generation errors — persisted to localStorage
  const [dismissedThemeError, setDismissedThemeError] = useState(() => {
    try { return localStorage.getItem(`dismissed-gen-error-theme-${slug}`) === 'true' } catch { return false }
  })
  const [dismissedImageErrorIds, setDismissedImageErrorIds] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem(`dismissed-gen-error-images-${slug}`)
      return stored ? new Set<number>(JSON.parse(stored)) : new Set()
    } catch { return new Set() }
  })

  const dismissThemeError = useCallback(() => {
    try { localStorage.setItem(`dismissed-gen-error-theme-${slug}`, 'true') } catch { /* localStorage unavailable */ }
    setDismissedThemeError(true)
  }, [slug])

  const dismissImageError = useCallback((id: number) => {
    setDismissedImageErrorIds(prev => {
      const next = new Set(prev).add(id)
      try { localStorage.setItem(`dismissed-gen-error-images-${slug}`, JSON.stringify([...next])) } catch { /* localStorage unavailable */ }
      return next
    })
  }, [slug])

  const dismissAllImageErrors = useCallback((ids: number[]) => {
    setDismissedImageErrorIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.add(id))
      try { localStorage.setItem(`dismissed-gen-error-images-${slug}`, JSON.stringify([...next])) } catch { /* localStorage unavailable */ }
      return next
    })
  }, [slug])

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const reload = useCallback(() => {
    themes.get(slug)
      .then(setTheme)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [slug])

  useEffect(() => { reload() }, [reload])

  // Poll while any image is PENDING or GENERATING, or theme is IN_PROGRESS
  useEffect(() => {
    const active = theme?.generationStatus === 'IN_PROGRESS' ||
      theme?.images.some((i) => i.generationStatus === 'PENDING' || i.generationStatus === 'GENERATING')

    if (active && !pollRef.current) {
      pollRef.current = setInterval(reload, 3000)
    } else if (!active && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
  }, [theme, reload])

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) { setUploadError('Selecciona una imagen'); return }
    if (!imageForm.wordEs) {
      setUploadError('El nombre en español es obligatorio')
      return
    }

    setUploading(true)
    setUploadError(null)

    try {
      const { uploadUrl, publicUrl, key } = await uploads.presign(file.name, file.type as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif', slug)
      await uploads.putFile(uploadUrl, file)
      await themes.addImage(slug, {
        url:          publicUrl,
        s3Key:        key,
        wordEs:       imageForm.wordEs,
        wordEn:       imageForm.wordEn   || undefined,
        altEs:        imageForm.altEs    || undefined,
        altEn:        imageForm.altEn    || undefined,
        definitionEs: imageForm.definitionEs || undefined,
        definitionEn: imageForm.definitionEn || undefined,
      })

      setImageForm(emptyImageForm())
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      setShowForm(false)
      dismissThemeError()
      dismissAllImageErrors(theme?.images.map(i => i.id) ?? [])
      reload()
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Error al subir la imagen')
    } finally {
      setUploading(false)
    }
  }

  const handleBatchFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    setBatchEntries(files.map(f => ({
      file:    f,
      wordEs:  f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
      preview: URL.createObjectURL(f),
      status:  'pending',
    })))
    setBatchError(null)
  }

  const handleBatchUpload = async () => {
    if (batchEntries.length === 0) return
    if (batchEntries.some(e => !e.wordEs.trim())) {
      setBatchError('Todos los nombres en español son obligatorios')
      return
    }

    setBatchUploading(true)
    setBatchError(null)

    const uploaded: Array<{ url: string; s3Key: string; wordEs: string }> = []

    for (let i = 0; i < batchEntries.length; i++) {
      const entry = batchEntries[i]
      setBatchEntries(prev => prev.map((e, idx) => idx === i ? { ...e, status: 'uploading' } : e))
      try {
        const { uploadUrl, publicUrl, key } = await uploads.presign(
          entry.file.name,
          entry.file.type as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif',
          slug,
        )
        await uploads.putFile(uploadUrl, entry.file)
        uploaded.push({ url: publicUrl, s3Key: key, wordEs: entry.wordEs.trim() })
        setBatchEntries(prev => prev.map((e, idx) => idx === i ? { ...e, status: 'done' } : e))
      } catch (err: unknown) {
        setBatchEntries(prev => prev.map((e, idx) =>
          idx === i ? { ...e, status: 'error', error: err instanceof Error ? err.message : 'Error' } : e
        ))
      }
    }

    if (uploaded.length > 0) {
      try {
        await themes.batchAddImages(slug, uploaded)
      } catch (err: unknown) {
        setBatchError(err instanceof Error ? err.message : 'Error al registrar las imágenes')
        setBatchUploading(false)
        return
      }
    }

    setBatchUploading(false)
    setBatchEntries([])
    if (batchFileRef.current) batchFileRef.current.value = ''
    setShowForm(false)
    dismissThemeError()
    dismissAllImageErrors(theme?.images.map(i => i.id) ?? [])
    reload()
  }

  const handleDelete = async (image: ThemeImage) => {
    if (!confirm(`¿Eliminar la imagen "${image.wordEs}"?`)) return
    try {
      await themes.deleteImage(slug, image.id)
      setTheme((prev) => prev ? { ...prev, images: prev.images.filter((i) => i.id !== image.id) } : prev)
    } catch { /* intentionally empty */ }
  }

  const handleSetCover = async (url: string) => {
    try {
      await themes.update(slug, { coverUrl: url })
      setTheme((prev) => prev ? { ...prev, coverUrl: url } : prev)
    } catch { /* intentionally empty */ }
  }

  const handleTogglePublished = async () => {
    if (!theme) return
    const action = theme.published ? 'despublicar' : 'publicar'
    if (!confirm(`¿Quieres ${action} el tema "${theme.nameEs}"?`)) return
    try {
      await themes.update(slug, { published: !theme.published })
      setTheme((prev) => prev ? { ...prev, published: !prev.published } : prev)
    } catch { /* intentionally empty */ }
  }

  const cancelImageEdit = () => { setEditingImageId(null); setImageEditError(null) }

  const handleGenerateItems = async (e: React.FormEvent) => {
    e.preventDefault()
    setGenItemsLoading(true)
    setGenItemsError(null)
    try {
      await themes.generateItems(slug, {
        count:       genItemsCount,
        extraPrompt: genItemsPrompt.trim() || undefined,
      })
      setShowGenItems(false)
      setGenItemsPrompt('')
      reload()
    } catch (err: unknown) {
      setGenItemsError(err instanceof Error ? err.message : 'Error al generar objetos')
    } finally {
      setGenItemsLoading(false)
    }
  }

  const saveImageEdit = async (id: number) => {
    if (!imageEditForm.wordEs || !imageEditForm.wordEn || !imageEditForm.altEs || !imageEditForm.altEn) {
      setImageEditError('Nombre (ES/EN) y alt text (ES/EN) son obligatorios')
      return
    }
    setImageEditSaving(true)
    setImageEditError(null)
    try {
      const updated = await themes.updateImage(slug, id, {
        wordEs:       imageEditForm.wordEs,
        wordEn:       imageEditForm.wordEn,
        altEs:        imageEditForm.altEs,
        altEn:        imageEditForm.altEn,
        definitionEs: imageEditForm.definitionEs || undefined,
        definitionEn: imageEditForm.definitionEn || undefined,
        value:        imageEditForm.value !== '' ? parseInt(imageEditForm.value, 10) : null,
      })
      setTheme((prev) => prev
        ? { ...prev, images: prev.images.map((i) => i.id === id ? updated : i) }
        : prev
      )
      setEditingImageId(null)
    } catch (err: unknown) {
      setImageEditError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setImageEditSaving(false)
    }
  }

  if (loading) return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>← Temas</button>
      </header>
      <div style={styles.content}><p style={styles.emptyText}>Cargando…</p></div>
    </div>
  )

  if (!theme) return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>← Temas</button>
      </header>
      <div style={styles.content}><p style={styles.errorText}>Tema no encontrado.</p></div>
    </div>
  )

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>← Temas</button>
        <h1 style={styles.headerTitle}>{theme.nameEs}</h1>
        <select
          value={theme.requiredTier}
          onChange={async (e) => {
            const t = e.target.value as 'BASIC' | 'PLUS' | 'PREMIUM'
            await themes.update(slug, { requiredTier: t }).catch(() => {})
            setTheme((prev) => prev ? { ...prev, requiredTier: t } : prev)
          }}
          style={{ ...styles.input, width: 'auto', marginLeft: 'auto', fontSize: '0.875rem', padding: '4px 8px', height: 'auto' }}
        >
          <option value="BASIC">BASIC</option>
          <option value="PLUS">PLUS</option>
          <option value="PREMIUM">PREMIUM</option>
        </select>
        <span style={{ ...styles.statusBadge, ...(theme.published ? styles.statusPublished : styles.statusDraft) }}>
          {theme.published ? 'Publicado' : 'Borrador'}
        </span>
        <button onClick={handleTogglePublished} style={styles.toggleBtn} title={theme.published ? 'Despublicar' : 'Publicar'}>
          {theme.published ? <FiPause size={14} /> : <FiPlay size={14} />}
        </button>
      </header>

      <div style={styles.content}>
        {/* FAILED theme error banner */}
        {theme.generationStatus === 'FAILED' && theme.generationError && !dismissedThemeError && (
          <div style={styles.errorBox}>
            <div style={styles.errorBoxHeader}>
              <span style={styles.errorBoxTitle}>Error de generación</span>
              <button onClick={dismissThemeError} style={styles.errorBoxClose} aria-label="Cerrar error">
                <FiX size={16} />
              </button>
            </div>
            <pre style={styles.errorBoxMessage}>{theme.generationError}</pre>
            {theme.generationPrompt && (
              <p style={{ ...styles.errorBoxPrompt, marginTop: 8 }}>
                Prompt: <em>{theme.generationPrompt}</em>
              </p>
            )}
          </div>
        )}

        {/* Images section */}
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            Imágenes <span style={styles.countLabel}>({theme.images.length})</span>
          </h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => { setShowGenItems((v) => !v); setShowForm(false) }}
              style={styles.aiBtn}
              disabled={theme.generationStatus === 'IN_PROGRESS'}
            >
              {showGenItems ? <><FiX size={13} style={{ marginRight: 4 }} />Cancelar</> : '✨ Generar objetos'}
            </button>
            <button
              onClick={() => { setShowForm(v => v === 'single' ? false : 'single'); setShowGenItems(false) }}
              style={styles.newBtn}
            >
              {showForm === 'single' ? <><FiX size={13} style={{ marginRight: 4 }} />Cancelar</> : '+ Añadir imagen'}
            </button>
            <button
              onClick={() => { setShowForm(v => v === 'batch' ? false : 'batch'); setShowGenItems(false); setBatchEntries([]) }}
              style={{ ...styles.newBtn, background: '#E8F4EA' }}
            >
              {showForm === 'batch' ? <><FiX size={13} style={{ marginRight: 4 }} />Cancelar</> : <><FiUpload size={13} style={{ marginRight: 4 }} />Subir en lote</>}
            </button>
          </div>
        </div>

        {/* Generate items panel */}
        {showGenItems && (
          <form onSubmit={handleGenerateItems} style={styles.createForm}>
            <div style={styles.formRow}>
              <div style={styles.formField}>
                <label style={styles.label}>Cantidad de objetos</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  style={{ ...styles.input, width: 80 }}
                  value={genItemsCount}
                  onChange={(e) => setGenItemsCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                />
              </div>
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Prompt extra (opcional)</label>
              <input
                style={styles.input}
                placeholder='Ej: "añade objetos de invierno" o "quiero incluir un abrigo específicamente"'
                value={genItemsPrompt}
                onChange={(e) => setGenItemsPrompt(e.target.value)}
              />
            </div>
            {genItemsError && <p style={styles.errorText}>{genItemsError}</p>}
            <button type="submit" disabled={genItemsLoading} style={styles.submitBtn}>
              {genItemsLoading ? 'Iniciando…' : '✨ Generar'}
            </button>
          </form>
        )}

        {/* Single upload form */}
        {showForm === 'single' && (
          <form onSubmit={handleUpload} style={styles.createForm}>
            <div style={styles.filePickRow}>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                style={styles.filePickBtn}
              >
                {file ? `✓ ${file.name}` : '📁 Seleccionar imagen'}
              </button>
              {file && <img src={URL.createObjectURL(file)} alt="" style={styles.preview} />}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formField}>
                <label style={styles.label}>Nombre en español *</label>
                <input style={styles.input} placeholder="Flor" value={imageForm.wordEs}
                  onChange={(e) => setImageForm((f) => ({ ...f, wordEs: e.target.value }))} autoFocus />
              </div>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#888', margin: '-4px 0 8px' }}>
              Usa ✨ para generar los demás textos con IA tras subir la imagen.
            </p>

            {uploadError && <p style={styles.errorText}>{uploadError}</p>}
            <button type="submit" disabled={uploading} style={styles.submitBtn}>
              {uploading ? 'Subiendo…' : 'Subir imagen'}
            </button>
          </form>
        )}

        {/* Batch upload form */}
        {showForm === 'batch' && (
          <div style={styles.createForm}>
            <div style={styles.filePickRow}>
              <input
                ref={batchFileRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={handleBatchFilePick}
              />
              <button
                type="button"
                onClick={() => batchFileRef.current?.click()}
                style={styles.filePickBtn}
              >
                {batchEntries.length > 0 ? `✓ ${batchEntries.length} imagen(es)` : '📁 Seleccionar imágenes'}
              </button>
            </div>

            {batchEntries.length > 0 && (
              <div style={{ border: '1px solid #E8DDD0', borderRadius: 8, marginTop: 8, overflow: 'hidden' }}>
                {batchEntries.map((entry, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                    borderBottom: idx < batchEntries.length - 1 ? '1px solid #F4EEE4' : undefined,
                    background: entry.status === 'done' ? '#F0F9F2' : entry.status === 'error' ? '#FFF0F0' : undefined,
                  }}>
                    <img src={entry.preview} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, flexShrink: 0, background: '#fff' }} />
                    <input
                      style={{ ...styles.inputSm, flex: 1, minWidth: 0 }}
                      placeholder="Nombre en español"
                      value={entry.wordEs}
                      onChange={(e) => setBatchEntries(prev => prev.map((en, i) => i === idx ? { ...en, wordEs: e.target.value } : en))}
                      disabled={entry.status === 'uploading' || entry.status === 'done'}
                    />
                    <span style={{ fontSize: '0.875rem', color: entry.status === 'done' ? '#2D6A4F' : entry.status === 'error' ? '#C0392B' : '#888', flexShrink: 0 }}>
                      {entry.status === 'uploading' ? '⏳' : entry.status === 'done' ? '✓' : entry.status === 'error' ? `✗ ${entry.error ?? ''}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {batchError && <p style={styles.errorText}>{batchError}</p>}
            <button
              onClick={handleBatchUpload}
              disabled={batchUploading || batchEntries.length === 0}
              style={{ ...styles.submitBtn, marginTop: 10 }}
            >
              {batchUploading ? 'Subiendo…' : `Subir ${batchEntries.length} imagen(es)`}
            </button>
          </div>
        )}

        {/* Image grid */}
        {theme.images.length === 0 ? (
          <p style={styles.emptyText}>
            No hay imágenes. Añade al menos 6 para poder jugar en modo fácil, 12 para difícil.
          </p>
        ) : (
          <div style={styles.imageGrid}>
            {theme.images.map((img) => (
              <div key={img.id} style={styles.imageCard}>
                <div style={styles.imageWrap}>
                  <img src={img.url} alt={img.altEs} style={styles.thumbLarge} />
                  {theme.coverUrl === img.url && (
                    <span style={styles.coverBadge}>Portada</span>
                  )}
                </div>

                {editingImageId === img.id ? (
                  /* ── Inline image edit form ── */
                  <div style={styles.imageEditForm}>
                    <div style={styles.imageEditRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Palabra ES</label>
                        <input style={styles.inputSm} value={imageEditForm.wordEs}
                          onChange={(e) => setImageEditForm((f) => ({ ...f, wordEs: e.target.value }))} autoFocus />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Palabra EN</label>
                        <input style={styles.inputSm} value={imageEditForm.wordEn}
                          onChange={(e) => setImageEditForm((f) => ({ ...f, wordEn: e.target.value }))} />
                      </div>
                    </div>
                    <div style={styles.imageEditRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Alt ES</label>
                        <input style={styles.inputSm} value={imageEditForm.altEs}
                          onChange={(e) => setImageEditForm((f) => ({ ...f, altEs: e.target.value }))} />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Alt EN</label>
                        <input style={styles.inputSm} value={imageEditForm.altEn}
                          onChange={(e) => setImageEditForm((f) => ({ ...f, altEn: e.target.value }))} />
                      </div>
                    </div>
                    <div style={styles.imageEditRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Def. ES</label>
                        <input style={styles.inputSm} placeholder="(opcional)" value={imageEditForm.definitionEs}
                          onChange={(e) => setImageEditForm((f) => ({ ...f, definitionEs: e.target.value }))} />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Def. EN</label>
                        <input style={styles.inputSm} placeholder="(optional)" value={imageEditForm.definitionEn}
                          onChange={(e) => setImageEditForm((f) => ({ ...f, definitionEn: e.target.value }))} />
                      </div>
                    </div>
                    <div style={styles.imageEditRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Valor (céntimos)</label>
                        <input style={styles.inputSm} type="number" min="0" placeholder="100 = 1€" value={imageEditForm.value}
                          onChange={(e) => setImageEditForm((f) => ({ ...f, value: e.target.value }))} />
                      </div>
                    </div>
                    {imageEditError && <p style={{ ...styles.errorText, margin: '4px 0 0' }}>{imageEditError}</p>}
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button
                        onClick={() => saveImageEdit(img.id)}
                        disabled={imageEditSaving}
                        style={styles.imageEditSaveBtn}
                      >
                        {imageEditSaving ? '…' : 'Guardar'}
                      </button>
                      <button onClick={cancelImageEdit} style={styles.imageEditCancelBtn}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Normal image card body ── */
                  <>
                    <div style={styles.imageCardBody}>
                      {(img.generationStatus === 'PENDING' || img.generationStatus === 'GENERATING') && (
                        <span style={{
                          ...styles.statusBadge,
                          fontSize: '0.875rem',
                          display: 'inline-block',
                          marginBottom: 4,
                          ...styles.statusGenerating,
                        }}>
                          {img.generationStatus === 'PENDING'    ? '⏳ Pendiente' : '⏳ Generando'}
                        </span>
                      )}
                      {img.generationStatus === 'FAILED' && !dismissedImageErrorIds.has(img.id) && (
                        <div style={styles.errorBox}>
                          <div style={styles.errorBoxHeader}>
                            <span style={styles.errorBoxTitle}>Error de generación</span>
                            <button onClick={() => dismissImageError(img.id)} style={styles.errorBoxClose} aria-label="Cerrar error">
                              <FiX size={16} />
                            </button>
                          </div>
                          <pre style={styles.errorBoxMessage}>{img.generationError ?? 'La generación falló sin un mensaje de error específico.'}</pre>
                        </div>
                      )}
                      <p style={styles.imageWord}>{img.wordEs} / {img.wordEn}</p>
                      {img.definitionEs && (
                        <p style={styles.imageDef}>{img.definitionEs}</p>
                      )}
                    </div>
                    {/* Actions: cover, edit, delete */}
                    <div style={styles.imageCardActions}>
                      <div style={styles.imageActionsRow}>
                        {img.url && theme.coverUrl !== img.url ? (
                          <button onClick={() => handleSetCover(img.url)} style={styles.imgIconBtn} title="Usar como portada">
                            ⭐
                          </button>
                        ) : (
                          <span style={styles.imgIconBtnSpacer} />
                        )}
                        <button
                          onClick={() => navigate(`/admin/themes/${slug}/objects/${img.id}`)}
                          style={styles.imgIconBtn}
                          title="Editar metadatos"
                          disabled={img.generationStatus === 'GENERATING' || img.generationStatus === 'PENDING'}
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(img)} style={{ ...styles.imgIconBtn, color: '#C0392B' }} title="Eliminar imagen">
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
